import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import moment, { Moment } from 'moment';
import { Button, DatePicker, Input, Radio, Select, Statistic, Table, Tag, Tooltip } from 'antd';
import { useHistory } from 'react-router';

import colors from '../../utils/colors';
import { USAGE } from '../../utils/constants/labels';
import PageHeader from '../common/layout/PageHeader';
import { handleErrorResponse } from '../../api/network';
import { usageApi } from '../../api/usageApi';
import { notify, NotificationType } from '../../utils/helpers';
import {
  CountRow,
  DayCount,
  TaskSourceRow,
  TimerModeRow,
  UsageGroup,
  UsageReport,
  UsageRow
} from '../../types/firstWeekTypes';
import { ACTION_AREAS, actionArea, actionName } from './actionNames';
import TrendLine from './graphs/TrendLine';

const { RangePicker } = DatePicker;

const DATE = 'YYYY-MM-DD';
// The server counts at most this many days at once (UsageReportService.MAX_DAYS), to keep it light.
const MAX_DAYS = 90;

// Quick ranges beside the date picker, each ending today (or last month's last day).
const PRESETS: { key: string; label: string; range: () => [Moment, Moment] }[] = [
  { key: '7', label: '7 days', range: () => [moment().subtract(6, 'days'), moment()] },
  { key: '30', label: '30 days', range: () => [moment().subtract(29, 'days'), moment()] },
  { key: '90', label: '90 days', range: () => [moment().subtract(89, 'days'), moment()] },
  { key: 'month', label: 'This month', range: () => [moment().startOf('month'), moment()] },
  {
    key: 'lastMonth',
    label: 'Last month',
    range: () => [
      moment()
        .subtract(1, 'month')
        .startOf('month'),
      moment()
        .subtract(1, 'month')
        .endOf('month')
    ]
  }
];

const sameDay = (a: Moment, b: Moment) => a.format(DATE) === b.format(DATE);

const GROUPS: { value: UsageGroup; label: string }[] = [
  { value: 'all', label: 'All students' },
  { value: 'website', label: 'Website students (paying)' },
  { value: 'school', label: 'School students' },
  { value: 'other', label: 'Free and invited accounts' }
];

// Where an action was done, from the log's source: the phone's own screens and the pages it shows inside itself are
// both "mobile"; AI apps are the MCP connection and the in-app assistant.
const PLACES: { key: string; title: string; sources: string[] }[] = [
  { key: 'web', title: 'Web', sources: ['web'] },
  { key: 'phone', title: 'Phone', sources: ['mobile'] },
  { key: 'ai', title: 'AI apps', sources: ['mcp', 'assistant'] },
  { key: 'autoplan', title: 'Auto-plan', sources: ['autoplan'] }
];

// How tasks were added (UsageReport.TaskSourceRow ways), and the four kinds the summary adds them up into.
const WAYS: { [way: string]: { label: string; kind: 'hand' | 'synced' | 'syllabus' | 'other' } } = {
  by_hand_web: { label: 'By hand on the web', kind: 'hand' },
  by_hand_phone: { label: 'By hand on the phone', kind: 'hand' },
  syllabus: { label: 'Syllabus PDF imported', kind: 'syllabus' },
  lms_canvas: { label: 'Synced from Canvas', kind: 'synced' },
  lms_blackboard: { label: 'Synced from Blackboard', kind: 'synced' },
  lms_brightspace: { label: 'Synced from Brightspace', kind: 'synced' },
  lms_moodle: { label: 'Synced from Moodle', kind: 'synced' },
  lms_other: { label: 'Synced from another course platform', kind: 'synced' },
  google: { label: 'Synced from Google Calendar', kind: 'synced' },
  ai_apps: { label: 'Added by AI apps or the in-app assistant', kind: 'other' },
  shared_course: { label: 'Copied from a shared course', kind: 'other' },
  reviews: { label: 'Auto-plan review sessions', kind: 'other' },
  rebuilt_shovel: { label: 'Added in Shovel (rebuilt: by hand, syllabus or shared)', kind: 'other' }
};

const KINDS: { kind: 'hand' | 'synced' | 'syllabus' | 'other'; title: string }[] = [
  { kind: 'hand', title: 'By hand' },
  { kind: 'synced', title: 'Synced (course platforms, Google)' },
  { kind: 'syllabus', title: 'Syllabus PDFs' },
  { kind: 'other', title: 'Other' }
];

// The study timer's words: modes, how a run ended, where it was started (UsageReport.TimerUsage).
const TIMER_WORDS: { [key: string]: string } = {
  stopwatch: 'Stopwatch',
  countdown: 'Countdown',
  stopped: 'Stopped',
  marked_used: 'Marked used',
  reset: 'Reset (time thrown away)',
  capped: 'Hit the 8-hour cap',
  session_popup: 'Session popup',
  task_details: "A task's sessions list",
  calendar: "Calendar's ▶",
  top_bar: 'Top-bar pill',
  phone: 'Phone app',
  ai_apps: 'AI apps',
  not_told: 'Web (not told where)'
};

const timerWord = (key: string) => TIMER_WORDS[key] || key.replace(/_/g, ' ');

const wayOf = (way: string) => WAYS[way] || { label: way.replace(/_/g, ' '), kind: 'other' as const };

const share = (part: number, whole: number) => (whole > 0 ? `${Math.round((part / whole) * 100)}%` : '–');

const placeShare = (row: UsageRow, sources: string[]) => {
  const total = Object.values(row.bySource).reduce((sum, times) => sum + times, 0);
  const part = sources.reduce((sum, source) => sum + (row.bySource[source] || 0), 0);
  return part === 0 ? '' : share(part, total);
};

const otherShare = (row: UsageRow) => {
  const known = PLACES.reduce((all, place) => all.concat(place.sources), [] as string[]);
  const total = Object.values(row.bySource).reduce((sum, times) => sum + times, 0);
  const other = Object.entries(row.bySource)
    .filter(([source]) => !known.includes(source))
    .reduce((sum, [, times]) => sum + times, 0);
  return other === 0 ? '' : share(other, total);
};

// Change in students from the period just before: "new" when nobody did it then.
const change = (row: UsageRow) => {
  if (row.previousStudents === 0) return row.students > 0 ? 'new' : '–';
  return Math.round(((row.students - row.previousStudents) / row.previousStudents) * 100);
};

const changeLabel = (row: UsageRow) => {
  const value = change(row);
  if (typeof value === 'string') return value;
  return `${value > 0 ? '+' : ''}${value}%`;
};

const changeColor = (row: UsageRow) => {
  const value = change(row);
  if (value === 'new') return '#389e0d';
  if (typeof value === 'string' || Math.abs(value) < 10) return 'inherit';
  return value > 0 ? '#389e0d' : '#cf1322';
};

const perDay = (days: DayCount[] | null | undefined, note?: (day: DayCount) => string) =>
  (days || []).map(day => ({ x: day.day, y: day.students, note: note ? note(day) : undefined }));

/** The graph under a Most used row: students who did that action each day. Loads when the row is opened. */
const ActionTrend: FC<{ event: string; group: UsageGroup; from: string; to: string }> = ({ event, group, from, to }) => {
  const history = useHistory();
  const [days, setDays] = useState<DayCount[] | null>(null);
  const [failed, setFailed] = useState('');

  useEffect(() => {
    let current = true;
    setDays(null);
    setFailed('');
    usageApi.getTrend(group, event, from, to).then(response => {
      if (!current) return;
      if (response.status === 503) setFailed('Another report is being built. Close and open the row in a minute.');
      else if (response.status === 504) setFailed('The graph took too long and was stopped. Try fewer days.');
      else if (!response.ok) handleErrorResponse(response, history);
      else setDays(response.data?.days || []);
    });
    return () => {
      current = false;
    };
  }, [event, group, from, to, history]);

  if (failed) return <Muted>{failed}</Muted>;
  if (!days) return <Muted>Loading the graph…</Muted>;
  return (
    <TrendBox>
      <Muted>Students who did it each day (UTC days)</Muted>
      <TrendLine
        height={170}
        series={[
          {
            id: 'Students',
            color: colors.primaryPurple,
            points: perDay(days, day => `${day.students.toLocaleString()} students, ${day.times.toLocaleString()} times`)
          }
        ]}
      />
    </TrendBox>
  );
};

// A Most used row, or (by area) the heading row before an area's actions.
type ListRow = UsageRow & { areaTitle?: string };

const Usage: FC = () => {
  const history = useHistory();
  const [group, setGroup] = useState<UsageGroup>('all');
  const [range, setRange] = useState<[Moment, Moment]>([moment().subtract(29, 'days'), moment()]);
  const [report, setReport] = useState<UsageReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [byArea, setByArea] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const response = await usageApi.getReport(group, range[0].format(DATE), range[1].format(DATE));
    setLoading(false);
    if (response.status === 503) {
      notify('The report is being built for another request. Try again in a minute.', NotificationType.WARNING);
      return;
    }
    if (response.status === 504) {
      // A query ran past the backend's 60-second limit and was stopped (ReportQueries), or the page waited too long.
      notify(
        "The report took too long and was stopped, so it can't slow the app. Try fewer days.",
        NotificationType.WARNING
      );
      return;
    }
    if (!response.ok) {
      handleErrorResponse(response, history);
      return;
    }
    if (response.data) setReport(response.data);
  }, [group, range, history]);

  useEffect(() => {
    load();
  }, [load]);

  const changeRange = (start: Moment, end: Moment) => {
    const earliest = end.clone().subtract(MAX_DAYS - 1, 'days');
    if (start.isBefore(earliest)) {
      notify(`Up to ${MAX_DAYS} days at a time: starting ${earliest.format(DATE)}`, NotificationType.WARNING);
      setRange([earliest, end]);
    } else {
      setRange([start, end]);
    }
  };

  const rows = useMemo(() => {
    const all = report?.rows || [];
    const words = search.trim().toLowerCase();
    return words
      ? all.filter(row =>
          actionName(row.event)
            .toLowerCase()
            .includes(words)
        )
      : all;
  }, [report, search]);

  // By area: a heading row before each area's actions, most students first inside it (the report's own order).
  const listRows: ListRow[] = useMemo(() => {
    if (!byArea) return rows;
    return ACTION_AREAS.flatMap(area => {
      const inArea = rows.filter(row => actionArea(row.event) === area.key);
      return inArea.length
        ? [
            { event: `area:${area.key}`, areaTitle: area.title, students: 0, times: 0, previousStudents: 0, bySource: {} },
            ...inArea
          ]
        : [];
    });
  }, [rows, byArea]);

  const active = report?.activeStudents || 0;
  const rebuiltOnly = report?.liveSince && moment(report.from).isBefore(moment(report.liveSince));

  const columns = [
    {
      title: 'Action',
      dataIndex: 'event',
      width: 260,
      render: (event: string) => <span>{actionName(event)}</span>
    },
    {
      title: 'Students',
      dataIndex: 'students',
      sorter: (a: UsageRow, b: UsageRow) => a.students - b.students,
      render: (students: number) => students.toLocaleString()
    },
    {
      title: (
        <Tooltip title="Share of the students who did anything at all in these dates (opening the app included).">
          <span>Of active ⓘ</span>
        </Tooltip>
      ),
      render: (row: UsageRow) => share(row.students, active)
    },
    {
      title: (
        <Tooltip title="Students, compared with the same number of days just before.">
          <span>Change ⓘ</span>
        </Tooltip>
      ),
      sorter: (a: UsageRow, b: UsageRow) => {
        const value = (row: UsageRow) => {
          const result = change(row);
          return result === 'new' ? Number.MAX_SAFE_INTEGER : typeof result === 'string' ? -Infinity : result;
        };
        return value(a) - value(b);
      },
      render: (row: UsageRow) => <span style={{ color: changeColor(row) }}>{changeLabel(row)}</span>
    },
    {
      title: 'Times',
      dataIndex: 'times',
      sorter: (a: UsageRow, b: UsageRow) => a.times - b.times,
      render: (times: number) => times.toLocaleString()
    },
    {
      title: 'Per student',
      render: (row: UsageRow) => (row.students > 0 ? (row.times / row.students).toFixed(1) : '–')
    },
    ...PLACES.map(place => ({
      title: place.title,
      render: (row: UsageRow) => <Muted>{placeShare(row, place.sources)}</Muted>
    })),
    {
      title: (
        <Tooltip title="Syllabus imports, rebuilt history (no place known) and anything not labelled yet.">
          <span>Other ⓘ</span>
        </Tooltip>
      ),
      render: (row: UsageRow) => <Muted>{otherShare(row)}</Muted>
    }
  ];

  const sources = report?.taskSources || [];
  const newTasks = sources.reduce((sum, row) => sum + row.tasks, 0);
  const sourceColumns = [
    { title: 'How', dataIndex: 'way', width: 300, render: (way: string) => wayOf(way).label },
    {
      title: 'Tasks',
      dataIndex: 'tasks',
      sorter: (a: TaskSourceRow, b: TaskSourceRow) => a.tasks - b.tasks,
      render: (tasks: number) => tasks.toLocaleString()
    },
    { title: 'Of new tasks', render: (row: TaskSourceRow) => share(row.tasks, newTasks) },
    {
      title: 'Students',
      dataIndex: 'students',
      sorter: (a: TaskSourceRow, b: TaskSourceRow) => a.students - b.students,
      render: (students: number) => students.toLocaleString()
    },
    {
      title: (
        <Tooltip title="Tasks, compared with the same number of days just before.">
          <span>Change ⓘ</span>
        </Tooltip>
      ),
      render: (row: TaskSourceRow) => {
        if (row.previousTasks === 0) return row.tasks > 0 ? <span style={{ color: '#389e0d' }}>new</span> : '–';
        const value = Math.round(((row.tasks - row.previousTasks) / row.previousTasks) * 100);
        const color = Math.abs(value) < 10 ? 'inherit' : value > 0 ? '#389e0d' : '#cf1322';
        return <span style={{ color }}>{`${value > 0 ? '+' : ''}${value}%`}</span>;
      }
    }
  ];

  const timer = report?.timer;
  const endingsTotal = (timer?.endings || []).reduce((sum, row) => sum + row.runs, 0);
  const startsTotal = (timer?.startedFrom || []).reduce((sum, row) => sum + row.runs, 0);
  const modeColumns = [
    { title: 'Mode', dataIndex: 'mode', width: 200, render: (mode: string) => timerWord(mode) },
    { title: 'Runs started', dataIndex: 'runs', render: (runs: number) => runs.toLocaleString() },
    { title: 'Students', dataIndex: 'students', render: (students: number) => students.toLocaleString() },
    {
      title: (
        <Tooltip title="Runs that ended keeping their time: stopped, or the session marked used while it ran.">
          <span>Ended with time ⓘ</span>
        </Tooltip>
      ),
      dataIndex: 'endedRuns',
      render: (runs: number) => runs.toLocaleString()
    },
    { title: 'Minutes timed', dataIndex: 'minutes', render: (minutes: number) => minutes.toLocaleString() },
    {
      title: 'Per run',
      render: (row: TimerModeRow) => (row.endedRuns > 0 ? `${Math.round(row.minutes / row.endedRuns)} min` : '–')
    }
  ];

  // By area, a heading row spans the whole table; sorting by a column would pull the actions out of their areas.
  const areaColumns = columns.map((column, index) => ({
    ...column,
    sorter: undefined,
    render: (value: any, row: ListRow, rowIndex: number) => {
      if (row.areaTitle) {
        return index === 0
          ? { children: <AreaTitle>{row.areaTitle}</AreaTitle>, props: { colSpan: columns.length } }
          : { props: { colSpan: 0 } };
      }
      return column.render ? (column.render as any)(value, row, rowIndex) : value;
    }
  }));

  const previousActive = report?.previousActiveStudents || 0;
  const activeChange = previousActive > 0 ? Math.round(((active - previousActive) / previousActive) * 100) : null;

  return (
    <Container>
      <PageHeader title={USAGE} />
      <Controls>
        <Select value={group} onChange={(value: UsageGroup) => setGroup(value)} style={{ width: 240 }}>
          {GROUPS.map(option => (
            <Select.Option key={option.value} value={option.value}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
        <Radio.Group
          value={
            PRESETS.find(preset => {
              const [start, end] = preset.range();
              return sameDay(start, range[0]) && sameDay(end, range[1]);
            })?.key
          }
          onChange={event => {
            const preset = PRESETS.find(option => option.key === event.target.value);
            if (preset) setRange(preset.range());
          }}
        >
          {PRESETS.map(preset => (
            <Radio.Button key={preset.key} value={preset.key}>
              {preset.label}
            </Radio.Button>
          ))}
        </Radio.Group>
        <RangePicker
          value={range}
          allowClear={false}
          onChange={values => values && values[0] && values[1] && changeRange(values[0], values[1])}
        />
        <Button onClick={load} loading={loading}>
          Refresh
        </Button>
        {report && <Muted>Updated {moment(report.generatedAt).fromNow()}</Muted>}
      </Controls>

      <Section>
        <Statistic
          title="Active students"
          value={active}
          suffix={
            activeChange === null ? (
              undefined
            ) : (
              <Muted style={{ fontSize: 14, marginLeft: 12 }}>
                {activeChange > 0 ? '+' : ''}
                {activeChange}% vs the {moment(report?.from).diff(moment(report?.previousFrom), 'days')} days before
              </Muted>
            )
          }
        />
        {!!report?.activeByDay?.length && (
          <div style={{ maxWidth: 900, marginTop: 8 }}>
            <Muted>Each day (UTC days: they turn at 8 pm New York time)</Muted>
            <TrendLine
              series={[
                {
                  id: 'Active students',
                  color: colors.primaryPurple,
                  points: perDay(report.activeByDay, day => `${day.students.toLocaleString()} active students`)
                }
              ]}
            />
          </div>
        )}
      </Section>

      <Section>
        <SectionTitle>How tasks were added</SectionTitle>
        <Hint>
          Every new task in these dates and where it came from: typed by the student, synced from a course platform or
          Google, imported from a syllabus PDF, or the rest. Synced tasks arrive when the app checks the platform, so they
          count on the day they arrive. Undone additions don&apos;t count.
        </Hint>
        <Kinds>
          {KINDS.map(({ kind, title }) => {
            const tasks = sources.filter(row => wayOf(row.way).kind === kind).reduce((sum, row) => sum + row.tasks, 0);
            return (
              <Statistic
                key={kind}
                title={title}
                value={tasks}
                suffix={<Muted style={{ fontSize: 14, marginLeft: 8 }}>{share(tasks, newTasks)}</Muted>}
              />
            );
          })}
        </Kinds>
        <Table
          size="small"
          loading={loading}
          rowKey={(row: TaskSourceRow) => row.way}
          columns={sourceColumns}
          dataSource={sources}
          pagination={false}
          scroll={{ x: 'max-content' }}
        />
      </Section>

      <Section>
        <SectionTitle>Timer</SectionTitle>
        <Hint>
          Study timer runs in these dates, by mode (the student&apos;s saved choice: stopwatch until the timer redesign
          ships), how they ended and where they were started.
        </Hint>
        <Table
          size="small"
          loading={loading}
          rowKey={(row: TimerModeRow) => row.mode}
          columns={modeColumns}
          dataSource={timer?.modes || []}
          pagination={false}
          scroll={{ x: 'max-content' }}
        />
        <Kinds style={{ marginTop: 16 }}>
          <div>
            <SubTitle>How runs ended</SubTitle>
            {(timer?.endings || []).map((row: CountRow) => (
              <Line key={row.key}>
                {timerWord(row.key)}: {row.runs.toLocaleString()} <Muted>{share(row.runs, endingsTotal)}</Muted>
              </Line>
            ))}
            {!timer?.endings?.length && <Muted>No runs ended in these dates.</Muted>}
          </div>
          <div>
            <SubTitle>Where they were started</SubTitle>
            {(timer?.startedFrom || []).map((row: CountRow) => (
              <Line key={row.key}>
                {timerWord(row.key)}: {row.runs.toLocaleString()} <Muted>{share(row.runs, startsTotal)}</Muted>
              </Line>
            ))}
            {!timer?.startedFrom?.length && <Muted>No runs started in these dates.</Muted>}
          </div>
        </Kinds>
      </Section>

      <Section>
        <SectionTitle>Most used</SectionTitle>
        <Hint>
          Everything students did in these dates (up to {MAX_DAYS} days), grouped by area with the most students first in
          each, or as one list. Undone actions, billing, and coaches acting for a student don&apos;t count. Places are the
          share of times from each. Click an action to see it day by day.
          {rebuiltOnly && (
            <>
              {' '}
              <Tag color="orange">Before {moment(report?.liveSince).format('MMM D, YYYY')}</Tag>
              only rebuilt history is there: sign-ups, tasks, completions and study sessions, with no place.
            </>
          )}
        </Hint>
        <Input.Search
          placeholder="Find an action"
          allowClear
          style={{ width: 280, marginBottom: 12 }}
          onChange={event => setSearch(event.target.value)}
        />
        <Radio.Group
          value={byArea ? 'area' : 'all'}
          onChange={event => setByArea(event.target.value === 'area')}
          style={{ marginLeft: 12, marginBottom: 12 }}
        >
          <Radio.Button value="area">By area</Radio.Button>
          <Radio.Button value="all">One list, most used first</Radio.Button>
        </Radio.Group>
        <Table
          size="small"
          loading={loading}
          rowKey={(row: ListRow) => row.event}
          rowClassName={(row: ListRow) => (row.areaTitle ? 'usage-area-row' : 'usage-action-row')}
          expandable={{
            expandRowByClick: true,
            rowExpandable: (row: ListRow) => !row.areaTitle,
            expandedRowRender: (row: ListRow) =>
              report && <ActionTrend event={row.event} group={group} from={report.from} to={report.to} />
          }}
          columns={byArea ? areaColumns : columns}
          dataSource={listRows}
          pagination={{ pageSize: 100, hideOnSinglePage: true }}
          scroll={{ x: 'max-content' }}
        />
      </Section>
    </Container>
  );
};

export default Usage;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  background-color: ${colors.white};
  position: relative;
  padding-bottom: 24px;

  .usage-action-row {
    cursor: pointer;
  }

  .usage-area-row > td {
    background-color: #fafafa;
    padding-top: 14px !important;
  }
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 24px 16px;
  flex-wrap: wrap;
`;

const Section = styled.div`
  padding: 8px 24px 24px;
`;

const SectionTitle = styled.h3`
  margin-bottom: 4px;
`;

const Hint = styled.p`
  color: rgba(0, 0, 0, 0.55);
  max-width: 900px;
  margin-bottom: 12px;
`;

const SubTitle = styled.h4`
  margin-bottom: 6px;
`;

const Line = styled.div`
  margin-bottom: 2px;
`;

const Kinds = styled.div`
  display: flex;
  gap: 48px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const TrendBox = styled.div`
  max-width: 900px;
  padding: 4px 0 8px;
`;

const AreaTitle = styled.span`
  font-weight: 600;
`;

const Muted = styled.span`
  color: rgba(0, 0, 0, 0.45);
`;
